import { TAlertType } from "../constants/alertType";
import { TThresholdType } from "app/types/alert";

export interface IVitalParameterThreshold {
  id: string;
  deleted_at: string | null;
  restored_at: string | null;
  created_at: string;
  updated_at: string;
  name: string;
  unit: string;
  lower_max_limit: number | null;
  lower_min_limit: number | null;
  upper_max_limit: number | null;
  upper_min_limit: number | null;
  threshold_type: string;
  vital_parameter: string;
}

export interface IVitalParameter {
  id: string;
  name: string;
  monitoring_parameters: IVitalParameterThreshold[];
}

export interface IMeasurementData {
  type: string;
  measurement: Record<string, number>;
  timestamp: string;
  telemonitoringPathwayId?: string;
  telemonitoring_pathway_id?: string;
}

export interface IPathwayVitalParameter {
  vital_parameter: IVitalParameter;
}



export const extractMeasurementValues = (measurementData: IMeasurementData) => {
  const { type, measurement } = measurementData;

  if (type === 'blood_pressure') {
    return {
      systolic: measurement.systolic,
      diastolic: measurement.diastolic,
      unit: 'mmHg'
    };
  }

  if (type === 'ecg') {
    return {
      afib: measurement.afib,
      unit: ''
    };
  }

  if (type === 'heart_rate') {
    const heartRateValue = measurement.heart_rate || measurement.heartRate;
    return {
      heartRate: heartRateValue,
      unit: 'bpm'
    };
  }

  if (type === 'spo2') {
    return {
      saturation: measurement.saturation,
      unit: '%'
    };
  }

  if (type === 'temperature') {
    return {
      temperature: measurement.temperature,
      unit: '°C'
    };
  }

  if (type === 'weight') {
    return {
      weight: measurement.weight,
      unit: 'Kg'
    };
  }

  if (type === 'dyspnea') {
    return {
      dyspnea: measurement.dyspnea,
      unit: ''
    };
  }

  if (type === 'step') {
    return {
      step: measurement.amount,
      unit: ''
    };
  }

  const [key, value] = Object.entries(measurement).find(([k]) => typeof measurement[k] === 'number') || [];
  const unit = getUnitForMeasurementType(type, key || '');

  return {
    [key || 'value']: value,
    unit
  };
};

type TThresholdResult = { alertType: TAlertType; thresholdType: TThresholdType | undefined };

const toSnakeCase = (str: string) =>
  str.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);

const UNSUPPORTED_THRESHOLD_TYPES = ['weight_variation_percentage'];

const checkSingleThreshold = (value: number, param: IVitalParameterThreshold): TThresholdResult => {
  const { lower_min_limit, lower_max_limit, upper_min_limit, upper_max_limit, threshold_type } = param;

  if (UNSUPPORTED_THRESHOLD_TYPES.includes(threshold_type)) {
    return { alertType: 'green', thresholdType: undefined };
  }

  const checksLow = threshold_type === 'low' || threshold_type === 'high_low';
  const checksHigh = threshold_type === 'high' || threshold_type === 'high_low';

  if (checksLow && lower_max_limit !== null && value <= lower_max_limit) {
    return {
      alertType: lower_min_limit !== null && value <= lower_min_limit ? 'red' : 'yellow',
      thresholdType: 'low',
    };
  }

  if (checksHigh && upper_min_limit !== null && value >= upper_min_limit) {
    return {
      alertType: upper_max_limit !== null && value >= upper_max_limit ? 'red' : 'yellow',
      thresholdType: 'high',
    };
  }

  return { alertType: 'green', thresholdType: undefined };
};

export const checkMeasurementAlert = (
  measurement: Record<string, number>,
  monitoringParameters: IVitalParameterThreshold[]
): TThresholdResult => {
  const normalized = Object.fromEntries(
    Object.entries(measurement).map(([k, v]) => [toSnakeCase(k), v])
  );

  let worst: TThresholdResult = { alertType: 'green', thresholdType: undefined };

  for (const param of monitoringParameters) {
    const value = normalized[toSnakeCase(param.name)];
    if (value == null) continue;

    const result = checkSingleThreshold(value, param);
    if (result.alertType === 'red') return result;
    if (result.alertType === 'yellow' && worst.alertType === 'green') worst = result;
  }

  return worst;
};

const getUnitForMeasurementType = (_type: string, key: string): string => {
  const unitMap: Record<string, string> = {
    'heartRate': 'bpm',
    'systolic': 'mmHg',
    'diastolic': 'mmHg',
    'temperature': '°C',
    'saturation': '%',
    'weight': 'Kg',
    'step': 'steps',
    'dyspnea': 'score'
  };

  return unitMap[key] || '';
};
